import type { UserRole } from "@shared/schemas/enums";

import { SYSTEM_NAV } from "@/components/system/systemNav";
import {
  isFinanceRole,
  isProjectLead,
  isSuperadminRole,
  navItemsForRole,
  type NavItemId,
} from "@/lib/auth/roles";

// ---------------------------------------------------------------------------
// Die Ziele der Palette (⌘K) — «Gehe zu <Seite>» fuer jede Seite, die die
// Rolle oeffnen darf (NAV-4, 2026-09-04).
// ---------------------------------------------------------------------------
// Befund: die Palette kannte Home, Kunden, Projekte, System. Wer auf der
// Projekte-Seite stand, fand keinen Weg zu Aufraeumen, Aktivitaeten,
// Liquiditaet, Partner Portal, Hilfe, Einstellungen. Dabei ist ⌘K das eine
// Werkzeug fuer «ich weiss, wie es heisst, aber nicht wo es liegt».
//
// Quelle ist KEINE neue Liste:
//   1. die Blaetter der Leiste aus `navItemsForRole` (NAV-1) — Reihenfolge des
//      Geruests, Titel = Leisten-Wort (NAV-2), Beschreibung vom Blatt;
//   2. danach die Nav-losen Ziele — Seiten mit Route, aber ohne Reiter. Ihre
//      Sichtbarkeit traegt DIESELBEN Praedikate wie ihr Route-Guard
//      (RequireProjectLead → isProjectLead, RequireFinance → isFinanceRole,
//      RequireSuperadmin → isSuperadminRole, Business-Gate RequireOperatingRole
//      → nicht superadmin). Zeigt die Palette ein Ziel, das die Route auf Home
//      umleitet, ist das der Fehler — `palette-targets.test.ts` faengt ihn.
//
// Reihenfolge = Leiste, dann die Nav-losen (Prinzip 9). Die Tipp-Suche der
// Palette darf umsortieren, die Ruheliste nicht.
//
// Pipeline steht bewusst NICHT hier: seit NAV-3 ist /pipeline ein Redirect auf
// die Projektliste (Sortierung «Stillstand»), keine Seite — «Projekte» traegt
// «Pipeline» als Suchwort.

export interface PaletteTarget {
  readonly id: string;
  readonly to: string;
  // Der Seitentitel, Wort fuer Wort (NAV-2-Regel).
  readonly label: string;
  // Eine Zeile: was liegt dort.
  readonly description: string;
  // Suchwoerter neben dem Titel — u. a. die ASCII-Schreibweise der Umlaute,
  // damit «aufraeumen» getippt «Aufräumen» trifft.
  readonly keywords: readonly string[];
  // Nur fuer Leisten-Blaetter gesetzt: das Icon der Leiste (navIcons.ts).
  readonly navId?: NavItemId;
}

// Suchwoerter pro Blatt — der Titel selbst steht schon im Suchtext.
const NAV_KEYWORDS: Record<NavItemId, readonly string[]> = {
  home: ["Start", "Startseite", "Heute"],
  customers: ["Kunde", "Kontakte", "bexio"],
  projects: ["Projekt", "Leads", "Posteingang", "Pipeline"],
  earnings: ["Bonus", "Deckungsbeitrag", "DB"],
  invoices: ["Rechnung", "Faelligkeit", "bexio"],
  finanzen: ["Finanzen", "Finanz-Cockpit", "KPI", "Cash Runway", "DSO"],
  produkte: ["Produkt", "Artikel", "EK", "UVP", "Preise"],
  team: ["Mitarbeiter", "Rollen", "Tiers"],
  einstellungen: ["Settings", "Konfiguration", "OneDrive"],
  "partner-portal": ["Partner", "Portal", "Rabatt", "Anmeldungen"],
  aktivitaeten: ["Aktivitaeten", "Verlauf", "Audit", "Timeline"],
  leads: ["Lead"],
};

interface NavLosesZiel {
  readonly id: string;
  readonly to: string;
  readonly label: string;
  readonly description: string;
  readonly keywords: readonly string[];
  // Dasselbe Praedikat wie der Route-Guard (siehe Kopf-Kommentar).
  readonly sichtbar: (role: UserRole) => boolean;
}

// Das Business-Gate (RequireOperatingRole): der UN-vertretene Superadmin sieht
// nichts Operatives; sobald er vertritt, ist `role` die Rolle des Vertretenen.
function operativ(role: UserRole): boolean {
  return !isSuperadminRole(role);
}

const NAV_LOSE_ZIELE: readonly NavLosesZiel[] = [
  {
    id: "route:leads-aufraeumen",
    to: "/leads/aufraeumen",
    label: "Aufräumen",
    description: "Anfragen seit Wochen ohne Bewegung",
    keywords: ["aufraeumen", "Leads", "Triage", "Alt-Leads", "wegraeumen", "behalten"],
    // Route: RequireOperatingRole + RequireProjectLead.
    sichtbar: (role) => operativ(role) && isProjectLead(role),
  },
  {
    id: "route:finanzen-liquiditaet",
    to: "/finanzen/liquiditaet",
    label: "Liquiditäts-Cockpit · 13 Wochen",
    description: "Kontostand-Vorschau, Bestell-Ampel und Was-wäre-wenn",
    keywords: ["Liquiditaet", "Liquidität", "Geld", "Kasse", "Kontostand", "Finanzen"],
    // Route: RequireOperatingRole + RequireFinance.
    sichtbar: (role) => operativ(role) && isFinanceRole(role),
  },
  {
    id: "route:ladepark",
    to: "/ladepark",
    label: "Ladepark-Rechner",
    description: "Was ein Anschluss für LKW oder eine Überbauung lädt — EGS257, Kosten, Material",
    keywords: ["Ladepark", "LKW", "Truck", "Ladestation", "EGS257", "Batterie", "Wohnueberbauung", "ZEV", "Anschluss"],
    // Route: RequireOperatingRole (LADEPARK-5).
    sichtbar: operativ,
  },
  {
    id: "route:hilfe",
    to: "/hilfe",
    label: "Hilfe",
    description: "Kurzanleitung für deine Rolle, Erste Schritte, Begriffe",
    keywords: ["Help", "Anleitung", "Erste Schritte", "Glossar"],
    // Route: ohne Guard (H-4, alle Rollen).
    sichtbar: () => true,
  },
  {
    id: "route:was-ist-neu",
    to: "/was-ist-neu",
    label: "Was ist neu",
    description: "Was sich im OS für dich geändert hat — die letzten 60 Tage",
    keywords: ["Neuerungen", "Version", "Änderungen", "Release", "Neu"],
    // Route: ohne Guard (NEU-1, alle Rollen).
    sichtbar: () => true,
  },
  {
    id: "system:root",
    to: "/system",
    label: "System",
    description: "Cockpit — Hintergrund-Jobs, Audit-Spur, Integrationen an einem Ort",
    keywords: ["Superadmin", "Kommandostand", "Cockpit"],
    // Route: RequireSuperadmin.
    sichtbar: isSuperadminRole,
  },
  // Die Unterseiten des System-Menues — Labels aus dem Menue («System ·
  // Tokens»), damit «Tok» die Tokens findet und «System» alle zeigt. Cockpit
  // ist die Wurzel /system und steht oben, nicht ein zweites Mal.
  ...SYSTEM_NAV.flatMap((top) =>
    top.subs.map(
      (sub): NavLosesZiel => ({
        id: `system:${top.top}-${sub.value}`,
        to: sub.path,
        label: `System · ${sub.label}`,
        description: sub.description,
        keywords: [top.label, sub.value, "Superadmin"],
        sichtbar: isSuperadminRole,
      }),
    ),
  ),
  {
    id: "route:styleguide",
    to: "/styleguide",
    label: "Style-Guide",
    description: "Jede Komponente in allen Zuständen",
    keywords: ["Styleguide", "Komponenten", "Design"],
    // Route: RequireSuperadmin.
    sichtbar: isSuperadminRole,
  },
];

export function paletteTargetsForRole(role: UserRole): readonly PaletteTarget[] {
  const blaetter: PaletteTarget[] = navItemsForRole(role).map((item) => ({
    id: `nav:${item.id}`,
    to: item.to,
    label: item.label,
    description: item.description,
    keywords: NAV_KEYWORDS[item.id],
    navId: item.id,
  }));
  const navLos: PaletteTarget[] = NAV_LOSE_ZIELE.filter((ziel) => ziel.sichtbar(role)).map(
    ({ id, to, label, description, keywords }) => ({ id, to, label, description, keywords }),
  );
  return [...blaetter, ...navLos];
}
